/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { tv } from 'tailwind-variants';
// Local
import { Logo } from 'components/authorable/shared/site-structure/Header/HeaderDesktop';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { sendGTMEvent } from '@next/third-parties/google';
import { createNavClickGTMEvent, createNavToggleGTMEvent } from 'lib/utils/gtm-utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useClickOutside from 'lib/hooks/useClickOutside';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';
import { useHeader } from './HeaderContext';
import { getTestProps } from 'lib/testing/utils';
import useDictionary from 'lib/hooks/useDictionary';
import GlobalSearchInput from 'components/authorable/shared/search/GlobalSearchInput';
import { useIsTablet } from 'lib/hooks/useIsTablet';
import clsx from 'clsx';
const HeaderMobile = (props)=>{
    /*
   * Same GraphQL payload as HeaderDesktop: the datasource is nested under `fields.data.item`,
   * every field arrives as `{ jsonValue }` and every multilist as `{ targetItems }`.
   *
   * `logoLink` and `contactUsCTA` are not selected by the query yet, so both read as undefined
   * — the logo renders unlinked and the Contact Us CTA is hidden until they are added.
   */ const item = props?.fields?.data?.item;
    const logo = item?.logo?.jsonValue;
    const logoLink = item?.logoLink?.jsonValue;
    const contactUsCTA = item?.contactUsCTA?.jsonValue;
    const navigationList = item?.navigationList?.targetItems ?? [];
    const { getDictionaryValue } = useDictionary();
    const { asPath } = useHeader();
    const homeButtonText = getDictionaryValue('MobileHomeButton') || 'Home';
    const mainMenuText = getDictionaryValue('MobileMainMenu') || 'Main menu';
    const isHomeActive = asPath === '/' || asPath.toLowerCase() === logoLink?.value?.href?.toLowerCase();
    const headerRef = useRef(null);
    const menuContainerRef = useRef(null);
    const searchBoxContainerRef = useRef(null);
    const tier2PanelRef = useRef(null);
    const { isMobileMenuOpen, isMobileDropdownOpen, isMobileLanguageSelectorOpen, showSearch, setMobileDropdownOpen, setMobileMenuOpen, setShowSearch, setMobileLanguageSelectorOpen, closeMobileMenu, handleOverlayChange } = useHeader();
    const [activeTier1, setActiveTier1] = React.useState(null);
    const [activeTier2Dropdown, setActiveTier2Dropdown] = React.useState(0);
    const [previouslyFocusedTier1, setPreviouslyFocusedTier1] = React.useState(null);
    /*
   * STATE
   */ const isTabletDevice = useIsTablet();
    const isMobileVisible = isTabletDevice; // Mobile header is visible when on tablet or smaller
    const isDropdownOpen = isMobileDropdownOpen !== null || showSearch || isMobileMenuOpen || isMobileLanguageSelectorOpen;
    /*
   * EVENT HANDLERS
   */ const toggleHamburger = useCallback(()=>{
        const newMenuState = !isMobileMenuOpen;
        // `nav_open` on the way in, `nav_closed` on the way out.
        sendGTMEvent({
            ...createNavToggleGTMEvent({
                navOpen: newMenuState
            })
        });
        setMobileMenuOpen(newMenuState);
        // Always close search when toggling menu
        setShowSearch(false);
        if (!newMenuState) {
            setMobileDropdownOpen(null);
            setMobileLanguageSelectorOpen(false);
            setActiveTier1(null);
            setActiveTier2Dropdown(null);
        }
        handleOverlayChange();
    }, [
        isMobileMenuOpen,
        setMobileMenuOpen,
        setShowSearch,
        setMobileDropdownOpen,
        setMobileLanguageSelectorOpen,
        handleOverlayChange
    ]);
    const toggleSearch = useCallback(()=>{
        const newSearchState = !showSearch;
        // Close mobile menu if it's open
        if (isMobileMenuOpen) {
            setMobileMenuOpen(false);
            setActiveTier1(null);
            setActiveTier2Dropdown(null);
        }
        setShowSearch(newSearchState);
        setMobileDropdownOpen(null);
        handleOverlayChange();
    }, [
        isMobileMenuOpen,
        setMobileMenuOpen,
        setShowSearch,
        setMobileDropdownOpen,
        handleOverlayChange,
        showSearch
    ]);
    /*
   * LIFECYCLE
   */ useClickOutside(headerRef, isMobileVisible && (isDropdownOpen || showSearch), closeMobileMenu);
    useOnRouteChange(closeMobileMenu);
    // Focus search input and handle Escape key
    useEffect(()=>{
        if (showSearch && searchBoxContainerRef.current) {
            // Focus the first input inside the search box
            const input = searchBoxContainerRef.current.querySelector('input');
            if (input) {
                input.focus();
                // Add keydown listener for Escape key to close search
                const handleKeydown = (e)=>{
                    if (e.key === 'Escape') {
                        setShowSearch(false);
                    }
                };
                // Store ref in variable to avoid stale closure
                const currentRef = searchBoxContainerRef.current;
                // Add the event listener to the search container
                currentRef.addEventListener('keydown', handleKeydown);
                // Cleanup
                return ()=>{
                    currentRef?.removeEventListener('keydown', handleKeydown);
                };
            }
            // Explicitly return undefined if input is not found
            return undefined;
        }
        // Explicitly return undefined if showSearch is false or ref is not set
        return undefined;
    }, [
        showSearch,
        setShowSearch
    ]);
    // Focus first element when tier 2 becomes active
    useEffect(()=>{
        if (activeTier1 !== null && tier2PanelRef.current) {
            // Small delay to ensure the tier 2 panel is rendered
            setTimeout(()=>{
                // Find the first focusable element in the tier 2 panel
                const firstFocusable = tier2PanelRef.current?.querySelector('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            }, 150);
        }
    }, [
        activeTier1
    ]);
    /*
   * Rendering
   */ if (!item) return /*#__PURE__*/ _jsx(_Fragment, {});
    const { hamburgerWrapper, headerSection, languageWrapper, menuItems, menuNavItem, wrapper, searchButton, ctaButtonTier1, ctaButtonWrapper, rightActionsWrapper, tier1Panel, tier2Panel, searchBoxWrapper, searchBoxContainer, homeButton } = TAILWIND_VARIANTS({
        isOpen: isMobileMenuOpen,
        showTier2: activeTier1 !== null,
        isHomeActive,
        showSearch: showSearch
    });
    return (
        /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: wrapper(),
                id: "header-mobile",
                ref: headerRef,
                ...getTestProps(`header-mobile-wrapper`),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: headerSection(),
                        children: [
                            /*#__PURE__*/ _jsx(Logo, {
                                logo: logo,
                                logoLink: logoLink
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: rightActionsWrapper(),
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        className: searchButton(),
                                        "aria-expanded": !!showSearch,
                                        "aria-label": "Toggle Search",
                                        "aria-haspopup": "dialog",
                                        "aria-controls": showSearch ? 'mobile-header-search-dialog' : undefined,
                                        onClick: ()=>toggleSearch(),
                                        ...getTestProps(`header-search-button`),
                                        children: /*#__PURE__*/ _jsx(SvgIcon, {
                                            icon: "outline-search",
                                            fill: "none",
                                            size: "s",
                                            viewBox: "0 0 21 21",
                                            className: "text-component-button-secondary-border"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: hamburgerWrapper(),
                                        children: /*#__PURE__*/ _jsx(Hamburger, {
                                            openMenu: isMobileMenuOpen,
                                            toggleHamburger: toggleHamburger
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: languageWrapper(),
                        style: {
                            top: `${props.mobileHeaderTotalHeight + 4}px`,
                            height: `calc(100dvh - ${props.mobileHeaderTotalHeight + 8}px)`
                        },
                        ref: menuContainerRef,
                        ...(!isMobileMenuOpen && {
                            inert: true
                        }),
                        onKeyDown: (e)=>{
                            if (e.key === 'Tab' && activeTier1 === null) {
                                // Handle tab navigation within tier 1
                                const focusableElements = menuContainerRef.current?.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                                if (focusableElements && focusableElements.length > 0) {
                                    const lastElement = focusableElements[focusableElements.length - 1];
                                    if (!e.shiftKey && document.activeElement === lastElement) {
                                        // Tab from last element - cycle back to first
                                        e.preventDefault();
                                        const firstElement = focusableElements[0];
                                        firstElement.focus();
                                    }
                                // Allow Shift+Tab from first element to go back to hamburger/header
                                }
                            }
                        },
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: tier1Panel(),
                                ...(activeTier1 !== null && {
                                    inert: true
                                }),
                                children: [
                                    /*#__PURE__*/ _jsxs("ul", {
                                        className: menuItems(),
                                        children: [
                                            /*#__PURE__*/ _jsx("li", {
                                                className: menuNavItem(),
                                                children: /*#__PURE__*/ _jsx("button", {
                                                    className: homeButton(),
                                                    onClick: ()=>{
                                                        window.location.href = logoLink?.value?.href || '/';
                                                    },
                                                    "aria-current": isHomeActive ? 'page' : undefined,
                                                    ...getTestProps(`header-tier1-home-button`),
                                                    children: homeButtonText
                                                })
                                            }),
                                            navigationList && navigationList.map((item, index)=>{
                                                const megaMenuList = item.megaMenuList?.targetItems ?? [];
                                                const hasMegaMenu = (megaMenuList?.length ?? 0) > 0;
                                                const navigationLink = item.navigationLink?.jsonValue;
                                                if (!navigationLink?.value?.href && !hasMegaMenu) return null;
                                                return /*#__PURE__*/ _jsx(Tier1NavItem, {
                                                    item: item,
                                                    index: index,
                                                    hasMegaMenu: hasMegaMenu,
                                                    onNavigate: ()=>{
                                                        if (hasMegaMenu) {
                                                            setPreviouslyFocusedTier1(document.activeElement);
                                                            setActiveTier1(index);
                                                            setActiveTier2Dropdown(null); // Open first tier 2 item by default
                                                        }
                                                    }
                                                }, item.name);
                                            })
                                        ]
                                    }),
                                    contactUsCTA?.value?.href && /*#__PURE__*/ _jsx("div", {
                                        className: ctaButtonWrapper(),
                                        children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaComponentClass: "default",
                                            ctaVariant: "custom",
                                            className: ctaButtonTier1(),
                                            field: contactUsCTA,
                                            onClick: (e)=>{
                                                e.preventDefault();
                                                const href = contactUsCTA?.value?.href;
                                                if (href) {
                                                    window.location.href = href;
                                                }
                                            },
                                            suppressNewTabIcon: true,
                                            ...getTestProps(`header-cta-button`),
                                            children: contactUsCTA.value.text || 'Contact Us'
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: clsx(tier2Panel(), activeTier1 !== null && navigationList[activeTier1] ? 'block' : 'hidden'),
                                ref: tier2PanelRef,
                                children: navigationList?.map((item, index)=>/*#__PURE__*/ _jsx(Tier2Navigation, {
                                        item: item,
                                        onBack: ()=>{
                                            setActiveTier1(null);
                                            setActiveTier2Dropdown(null);
                                        },
                                        activeTier2Dropdown: activeTier2Dropdown,
                                        setActiveTier2Dropdown: setActiveTier2Dropdown,
                                        previouslyFocusedTier1: previouslyFocusedTier1,
                                        mainMenuText: mainMenuText,
                                        className: activeTier1 === index ? 'block' : 'hidden'
                                    }, item.name))
                            })
                        ]
                    }),
                    showSearch && /*#__PURE__*/ _jsx("div", {
                        id: "mobile-header-search-dialog",
                        className: searchBoxWrapper(),
                        ref: searchBoxContainerRef,
                        ...getTestProps(`header-search-box`),
                        children: /*#__PURE__*/ _jsx("div", {
                            className: searchBoxContainer(),
                            children: /*#__PURE__*/ _jsx(GlobalSearchInput, {
                                placeholder: getDictionaryValue('SearchKeywords') || 'Search Keywords',
                                onSearch: ()=>{
                                    setShowSearch(false);
                                },
                                onClear: ()=>{
                                // Search cleared
                                }
                            })
                        })
                    })
                ]
            })
        })
    );
};
// ========== TIER 1: Main Navigation (Top Level Menu Items) ==========
const Tier1NavItem = ({ item, index, hasMegaMenu, onNavigate })=>{
    const { isTier1Active, asPath } = useHeader();
    const navigationLink = item.navigationLink?.jsonValue;
    const navigationLinks = navigationLink?.value?.href;
    const megaMenuList = item.megaMenuList?.targetItems ?? [];
    // Use useMemo to recalculate when megaMenuList data or path changes
    const isActive = useMemo(()=>isTier1Active(navigationLinks, megaMenuList, hasMegaMenu), // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        navigationLinks,
        megaMenuList,
        hasMegaMenu,
        asPath
    ]);
    const { linkWrapper, tier1Button, menuNavItem } = TAILWIND_VARIANTS({
        isActive
    });
    if (hasMegaMenu) {
        return /*#__PURE__*/ _jsx("li", {
            className: menuNavItem(),
            children: /*#__PURE__*/ _jsxs("button", {
                className: tier1Button(),
                onClick: ()=>{
                    sendGTMEvent(createNavClickGTMEvent({
                        clickText: item.navigationTitle?.jsonValue?.value,
                        navLevel: 1,
                        navLocation: 'mobile_menu',
                        menuCat1: item.navigationTitle?.jsonValue?.value
                    }));
                    onNavigate();
                },
                "aria-label": item.navigationTitle?.jsonValue?.value || 'Navigation Item',
                ...getTestProps(`header-tier1-button-${index}`),
                children: [
                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                        field: item.navigationTitle?.jsonValue
                    }),
                    /*#__PURE__*/ _jsx(SvgIcon, {
                        fill: "currentColor",
                        icon: "arrow-dash-right",
                        size: "xs",
                        viewBox: "0 0 20 20"
                    })
                ]
            })
        });
    }
    if (navigationLink?.value?.href) {
        return /*#__PURE__*/ _jsx("li", {
            className: menuNavItem(),
            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                ctaComponentClass: "default",
                className: linkWrapper(),
                field: navigationLink,
                onClick: (e)=>{
                    e.preventDefault();
                    const href = navigationLink?.value?.href;
                    if (href) {
                        window.location.href = href;
                    }
                },
                suppressNewTabIcon: true,
                "aria-current": isActive ? 'page' : undefined,
                gtmEvent: createNavClickGTMEvent({
                    clickText: item.navigationTitle?.jsonValue?.value,
                    navLevel: 1,
                    navLocation: 'mobile_menu',
                    menuCat1: item.navigationTitle?.jsonValue?.value,
                    linkUrl: navigationLink?.value?.href
                }),
                children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                    field: item.navigationTitle?.jsonValue
                })
            })
        });
    }
    return null;
};
// ========== TIER 2: Category Menu (Second Level - Slides from right) ==========
const Tier2Navigation = ({ item, onBack, activeTier2Dropdown, setActiveTier2Dropdown, previouslyFocusedTier1, mainMenuText, className })=>{
    const tier2Ref = useRef(null);
    const { getDictionaryValue } = useDictionary();
    // Handle keyboard navigation within tier 2
    const handleKeyDown = (event)=>{
        if (event.key === 'Tab') {
            const focusableElements = tier2Ref.current?.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusableElements && focusableElements.length > 0) {
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];
                if (event.shiftKey && document.activeElement === firstElement) {
                    // Shift+Tab from first element - go back to tier 1
                    event.preventDefault();
                    if (previouslyFocusedTier1) {
                        previouslyFocusedTier1.focus();
                    }
                } else if (!event.shiftKey && document.activeElement === lastElement) {
                    // Tab from last element - cycle back to first element in tier 2
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        }
    };
    const handleBackClick = ()=>{
        // Return focus to the tier 1 button that opened this tier 2 BEFORE closing tier 2
        if (previouslyFocusedTier1) {
            previouslyFocusedTier1.focus();
        }
        onBack();
    };
    const megaMenuList = item.megaMenuList?.targetItems ?? [];
    const navigationLink = item.navigationLink?.jsonValue;
    // Get active state helpers from context
    const { isTier2Active, isTier3Active } = useHeader();
    const isTier2OverviewActive = isTier3Active(navigationLink?.value?.href);
    const { backButton, backButtonText, menuItems, tier2Item, tier2ItemButton: _tier2ItemButton, tier2ItemLink, tier2GroupedItem, tier3Wrapper, tier3List, tier3Item, tier3Link, tier2WithTier3Wrapper, tier2ToggleButton: _tier2ToggleButton, tier2FullToggleButton } = TAILWIND_VARIANTS({});
    return /*#__PURE__*/ _jsxs("div", {
        ref: tier2Ref,
        onKeyDown: handleKeyDown,
        className: className,
        children: [
            /*#__PURE__*/ _jsxs("button", {
                className: backButton(),
                onClick: handleBackClick,
                "aria-label": mainMenuText || 'Back',
                ...getTestProps(`header-back-button`),
                children: [
                    /*#__PURE__*/ _jsx(SvgIcon, {
                        fill: "currentColor",
                        icon: "arrow-dash-right",
                        size: "xs",
                        viewBox: "0 0 20 20",
                        className: "rotate-180"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: backButtonText(),
                        children: mainMenuText
                    })
                ]
            }),
            navigationLink?.value?.href && /*#__PURE__*/ _jsx("ul", {
                className: menuItems(),
                children: /*#__PURE__*/ _jsx("li", {
                    className: tier2Item(),
                    children: /*#__PURE__*/ _jsx("div", {
                        className: tier2WithTier3Wrapper(),
                        children: /*#__PURE__*/ _jsx("div", {
                            className: `${tier2GroupedItem()} min-h-[46px] ${isTier2OverviewActive ? 'bg-color-core-color-4-SkyBlue' : ''}`,
                            children: /*#__PURE__*/ _jsxs(LinkWrapper, {
                                ctaComponentClass: "default",
                                ctaVariant: "custom",
                                className: tier2ItemLink(),
                                field: navigationLink,
                                onClick: (e)=>{
                                    e.preventDefault();
                                    const href = navigationLink?.value?.href;
                                    if (href) {
                                        window.location.href = href;
                                    }
                                },
                                suppressNewTabIcon: true,
                                "aria-current": isTier2OverviewActive ? 'page' : undefined,
                                gtmEvent: createNavClickGTMEvent({
                                    clickText: item.navigationTitle?.jsonValue?.value,
                                    navLevel: 2,
                                    navLocation: 'mobile_menu',
                                    menuCat1: item.navigationTitle?.jsonValue?.value,
                                    linkUrl: navigationLink?.value?.href
                                }),
                                ...getTestProps(`header-tier2-overview`),
                                children: [
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        field: item.navigationTitle?.jsonValue
                                    }),
                                    ' ',
                                    getDictionaryValue('MobileOverviewText') || 'Overview'
                                ]
                            })
                        })
                    })
                })
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: menuItems() + ' mb-10',
                children: megaMenuList && megaMenuList.length === 1 ? (()=>{
                    const category = megaMenuList[0];
                    const megaMenuLinks = category.megaMenuLinks?.targetItems;
                    // Get all tier 3 links
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const items = megaMenuLinks || [];
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const allLinks = [];
                    items.forEach((item)=>{
                        if (item?.megaMenuCategory?.targetItems) {
                            allLinks.push(// eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ...item.megaMenuCategory.targetItems.map((link)=>({
                                    ...link,
                                    fromColumnGroup: true
                                })));
                        } else if (item?.link) {
                            allLinks.push(item);
                        }
                    });
                    // Render tier 3 links in tier 2 style (flat list)
                    return allLinks.map((link, linkIndex)=>{
                        const linkHref = link.link?.jsonValue?.value?.href;
                        const isActive = isTier3Active(linkHref);
                        const label = link.fromColumnGroup ? link.name : link.link?.jsonValue?.value?.text || link.name;
                        return /*#__PURE__*/ _jsx("li", {
                            className: tier2Item(),
                            children: /*#__PURE__*/ _jsx("div", {
                                className: tier2WithTier3Wrapper(),
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: `${tier2GroupedItem()} min-h-[46px] ${isActive ? 'bg-color-core-color-4-SkyBlue' : ''}`,
                                    children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaComponentClass: "default",
                                        ctaVariant: "custom",
                                        className: tier2ItemLink(),
                                        field: link.link?.jsonValue,
                                        onClick: (e)=>{
                                            e.preventDefault();
                                            const href = link.link?.jsonValue?.value?.href;
                                            if (href) {
                                                window.location.href = href;
                                            }
                                        },
                                        suppressNewTabIcon: true,
                                        "aria-current": isActive ? 'page' : undefined,
                                        gtmEvent: createNavClickGTMEvent({
                                            clickText: label,
                                            navLevel: 2,
                                            navLocation: 'mobile_menu',
                                            menuCat1: item.navigationTitle?.jsonValue?.value,
                                            menuCat2: label,
                                            linkUrl: link.link?.jsonValue?.value?.href
                                        }),
                                        ...getTestProps(`header-tier2-flat-link-${linkIndex}`),
                                        children: label
                                    })
                                })
                            })
                        }, linkIndex);
                    });
                })() : megaMenuList?.map((category, catIndex)=>{
                    const megaMenuLinks = category.megaMenuLinks?.targetItems;
                    const megaMenuCTA = category.megaMenuCTA?.jsonValue;
                    const categoryMenuCTA = megaMenuCTA;
                    // Handle two data structures:
                    // 1. Column groups with megaMenuCategory arrays (nested)
                    // 2. Direct links with link fields (flat)
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const items = megaMenuLinks || [];
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const allLinks = [];
                    items.forEach((item)=>{
                        // Check if this is a column group (has megaMenuCategory)
                        if (item?.megaMenuCategory?.targetItems) {
                            allLinks.push(// eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ...item.megaMenuCategory.targetItems.map((link)=>({
                                    ...link,
                                    fromColumnGroup: true
                                })));
                        } else if (item?.link) {
                            allLinks.push(item);
                        }
                    });
                    const hasLinks = allLinks.length > 0;
                    const isExpanded = activeTier2Dropdown === catIndex;
                    const isActive = isTier2Active(category);
                    const categoryLabel = category?.megaMenuTitle?.jsonValue?.value || category.name;
                    return /*#__PURE__*/ _jsx("li", {
                        className: tier2Item(),
                        children: hasLinks ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: tier2WithTier3Wrapper(),
                                    children: /*#__PURE__*/ _jsxs("button", {
                                        className: `${tier2FullToggleButton()} ${isExpanded ? 'bg-component-header-flyoutmenu-mobile-hoverbg' : ''}`,
                                        onClick: ()=>{
                                            // Reported on opening only; collapsing again is not a nav step.
                                            if (!isExpanded) {
                                                sendGTMEvent(createNavClickGTMEvent({
                                                    clickText: category.name,
                                                    navLevel: 2,
                                                    navLocation: 'mobile_menu',
                                                    menuCat1: item.navigationTitle?.jsonValue?.value,
                                                    menuCat2: category.name
                                                }));
                                            }
                                            setActiveTier2Dropdown(isExpanded ? null : catIndex);
                                        },
                                        "aria-expanded": isExpanded,
                                        "aria-label": `Toggle ${categoryLabel} submenu`,
                                        ...getTestProps(`header-tier2-toggle-${catIndex}`),
                                        children: [
                                            categoryLabel,
                                            /*#__PURE__*/ _jsx(SvgIcon, {
                                                className: `transition-transform duration-200 ${isExpanded ? 'rotate-0' : 'rotate-180'} text-color-core-color-1-Navy`,
                                                fill: "currentColor",
                                                icon: "chevron-up",
                                                size: "xs",
                                                viewBox: "0 0 24 24"
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: clsx(tier3Wrapper(), isExpanded ? 'block' : 'hidden'),
                                    children: /*#__PURE__*/ _jsxs("ul", {
                                        className: tier3List(),
                                        children: [
                                            categoryMenuCTA?.value?.href && /*#__PURE__*/ _jsx("li", {
                                                className: tier3Item(),
                                                children: /*#__PURE__*/ _jsxs(LinkWrapper, {
                                                    ctaComponentClass: "default",
                                                    ctaVariant: "custom",
                                                    className: `${tier3Link()} ${isActive ? 'bg-color-core-color-4-SkyBlue' : ''}`,
                                                    field: categoryMenuCTA,
                                                    onClick: (e)=>{
                                                        e.preventDefault();
                                                        const href = categoryMenuCTA?.value?.href;
                                                        if (href) {
                                                            window.location.href = href;
                                                        }
                                                    },
                                                    suppressNewTabIcon: true,
                                                    "aria-current": isActive ? 'page' : undefined,
                                                    gtmEvent: createNavClickGTMEvent({
                                                        clickText: category.name,
                                                        navLevel: 2,
                                                        navLocation: 'mobile_menu',
                                                        menuCat1: item.navigationTitle?.jsonValue?.value,
                                                        menuCat2: category.name,
                                                        linkUrl: categoryMenuCTA?.value?.href
                                                    }),
                                                    ...getTestProps(`header-tier3-overview-${catIndex}`),
                                                    children: [
                                                        categoryLabel,
                                                        ' ',
                                                        getDictionaryValue('MobileOverviewText') || 'Overview'
                                                    ]
                                                })
                                            }),
                                            allLinks.map((link, linkIndex)=>{
                                                const linkHref = link.link?.jsonValue?.value?.href;
                                                const isActive = isTier3Active(linkHref);
                                                const label = link.fromColumnGroup ? link.name : link.link?.jsonValue?.value?.text || link.name;
                                                return /*#__PURE__*/ _jsx("li", {
                                                    className: tier3Item(),
                                                    children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                                        ctaComponentClass: "default",
                                                        ctaVariant: "custom",
                                                        className: `${tier3Link()} ${isActive ? 'bg-color-core-color-4-SkyBlue' : ''}`,
                                                        field: link.link?.jsonValue,
                                                        onClick: (e)=>{
                                                            e.preventDefault();
                                                            const href = link.link?.jsonValue?.value?.href;
                                                            if (href) {
                                                                window.location.href = href;
                                                            }
                                                        },
                                                        suppressNewTabIcon: true,
                                                        "aria-current": isActive ? 'page' : undefined,
                                                        gtmEvent: createNavClickGTMEvent({
                                                            clickText: label,
                                                            navLevel: 3,
                                                            navLocation: 'mobile_menu',
                                                            menuCat1: item.navigationTitle?.jsonValue?.value,
                                                            menuCat2: category.name,
                                                            menuCat3: label,
                                                            linkUrl: linkHref
                                                        }),
                                                        ...getTestProps(`header-tier3-link-${catIndex}-${linkIndex}`),
                                                        children: label
                                                    })
                                                }, linkIndex);
                                            })
                                        ]
                                    })
                                })
                            ]
                        }) : // No Tier 3 links - render as direct link if CTA exists
                        categoryMenuCTA?.value?.href && /*#__PURE__*/ _jsx("div", {
                            className: tier2WithTier3Wrapper(),
                            children: /*#__PURE__*/ _jsx("div", {
                                className: `${tier2GroupedItem()} min-h-[46px] ${isActive ? 'bg-color-core-color-4-SkyBlue' : ''}`,
                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "default",
                                    ctaVariant: "custom",
                                    className: tier2ItemLink(),
                                    field: categoryMenuCTA,
                                    onClick: (e)=>{
                                        e.preventDefault();
                                        const href = categoryMenuCTA?.value?.href;
                                        if (href) {
                                            window.location.href = href;
                                        }
                                    },
                                    suppressNewTabIcon: true,
                                    "aria-current": isActive ? 'page' : undefined,
                                    ...getTestProps(`header-tier2-link-${catIndex}`),
                                    children: categoryLabel
                                })
                            })
                        })
                    }, category.name);
                })
            })
        ]
    });
};
const Hamburger = ({ openMenu, toggleHamburger })=>{
    const { hamburgerButton } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("button", {
        "aria-expanded": openMenu,
        "aria-label": "Toggle Menu",
        className: hamburgerButton(),
        onClick: ()=>toggleHamburger(),
        ...getTestProps(`header-hamburger-item`),
        children: !openMenu ? /*#__PURE__*/ _jsx(SvgIcon, {
            fill: "currentColor",
            icon: "hamburger-menu",
            size: "sm",
            viewBox: "0 0 32 32"
        }) : /*#__PURE__*/ _jsx(SvgIcon, {
            fill: "currentColor",
            icon: "menu-close",
            size: "sm",
            viewBox: "0 0 32 32"
        })
    });
};
const TAILWIND_VARIANTS = tv({
    slots: {
        // Tier panels
        tier1Panel: [
            'flex',
            'flex-col',
            'h-full',
            'transition-[transform,opacity]',
            'duration-300',
            'ease-in-out',
            'mt-spacing-spacing-12',
            'pb-spacing-spacing-24',
            'pb-[calc(1.5rem+env(safe-area-inset-bottom))]'
        ],
        tier2Panel: [
            'absolute',
            'inset-0',
            'bg-white',
            'overflow-y-auto',
            'overflow-x-hidden',
            'transition-[transform,opacity]',
            'duration-300',
            'ease-in-out',
            'pb-spacing-spacing-24',
            'pb-[calc(1.5rem+env(safe-area-inset-bottom))]'
        ],
        // ========== TIER 1 STYLES: Main navigation buttons ==========
        tier1Button: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'text-component-button-tertiary-text',
            'gap-2',
            'flex',
            'justify-between',
            'items-center',
            'w-full',
            'text-left',
            'hover:underline',
            'py-spacing-spacing-12',
            'px-spacing-spacing-12',
            'hover:bg-component-header-flyoutmenu-mobile-hoverbg',
            'focus:bg-component-header-flyoutmenu-mobile-hoverbg',
            'active:bg-color-core-color-4-SkyBlue',
            '!focus:ring-0',
            '!focus:ring-offset-0',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'rounded-border-radius-radius-1'
        ],
        // ========== TIER 2 STYLES: Category menu (back button, category links) ==========
        backButton: [
            'flex',
            'w-[94%]',
            'items-center',
            'leading-[22px]',
            'gap-spacing-spacing-12',
            'px-spacing-spacing-16',
            'py-spacing-spacing-12',
            'font-semibold',
            'text-color-text-text',
            'hover:bg-component-header-flyoutmenu-mobile-hoverbg',
            'focus:bg-component-header-flyoutmenu-mobile-hoverbg',
            'active:bg-color-core-color-4-SkyBlue',
            '!focus:ring-0',
            '!focus:ring-offset-0',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'rounded-border-radius-radius-1',
            'mx-spacing-spacing-12',
            'mt-spacing-spacing-12',
            'mb-spacing-spacing-8',
            'max-w-full'
        ],
        backButtonText: [
            'text-typography-font-size-body-medium'
        ],
        tier2Title: [
            'font-semibold',
            'text-typography-font-size-body-large'
        ],
        tier2Item: [],
        tier2ItemButton: [
            'w-full',
            'flex',
            'justify-between',
            'items-center',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'text-component-button-tertiary-text',
            'text-left'
        ],
        tier2ItemLink: [
            'block',
            'flex-1',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            '!leading-[22px]',
            'text-component-button-tertiary-text',
            'cursor-pointer',
            'hover:underline',
            'transition-colors',
            'duration-200',
            'active:bg-color-core-color-4-SkyBlue',
            '!focus:ring-0',
            '!focus:ring-offset-0',
            'focus:outline-none'
        ],
        // ========== TIER 3 STYLES: Sub-category links (accordion dropdown) ==========
        tier3Wrapper: [],
        tier3List: [
            'flex',
            'flex-col',
            'py-2',
            'pl-[56px]',
            'pr-spacing-spacing-12',
            'gap-spacing-spacing-4'
        ],
        tier3Item: [],
        tier3Link: [
            'block',
            'py-spacing-spacing-12',
            'px-spacing-spacing-12',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            '!leading-[22px]',
            'text-component-button-tertiary-text',
            'cursor-pointer',
            'hover:underline',
            'transition-colors',
            'duration-200',
            'hover:bg-component-header-flyoutmenu-mobile-hoverbg',
            'focus:bg-component-header-flyoutmenu-mobile-hoverbg',
            'active:bg-color-core-color-4-SkyBlue',
            '!focus:ring-0',
            '!focus:ring-offset-0',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'rounded-border-radius-radius-1'
        ],
        // Contact Us button (Tier 1)
        ctaButtonTier1: [
            'bg-component-button-secondary-bg',
            'border',
            'border-component-button-secondary-border',
            'text-component-button-secondary-text',
            'w-full',
            'px-6',
            'py-3',
            'rounded-lg',
            'font-typography-font-family-body',
            'font-semibold',
            'text-typography-font-size-body-medium',
            'flex',
            'items-center',
            'justify-center',
            'hover:bg-component-button-secondary-bg-hover',
            'hover:border-component-button-secondary-border-hover',
            'hover:text-component-button-secondary-text-hover',
            'focus:bg-component-button-secondary-bg-focused',
            'focus:border-component-button-secondary-border-focused',
            'focus:text-component-button-secondary-text-focused',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'transition-[background-color,border-color,color]',
            'duration-200'
        ],
        ctaButtonWrapper: [
            'pb-4',
            'px-4',
            'mt-spacing-spacing-40'
        ],
        // Search box
        searchBoxWrapper: [
            'bg-color-core-color-1-Navy',
            'px-0',
            'py-4',
            'border-t',
            'border-color-border-border'
        ],
        searchBoxContainer: [
            'm-auto',
            'max-w-screen-sm',
            'px-4'
        ],
        // Tier 2 wrappers
        tier2WithTier3Wrapper: [
            'flex',
            'items-center',
            'justify-between',
            'pl-[44px]',
            'pr-spacing-spacing-12',
            'py-spacing-spacing-4'
        ],
        tier2GroupedItem: [
            'flex',
            'items-center',
            'justify-between',
            'hover:bg-component-header-flyoutmenu-mobile-hoverbg',
            'focus-within:bg-component-header-flyoutmenu-mobile-hoverbg',
            'active:bg-color-core-color-4-SkyBlue',
            'has-[a:focus]:outline',
            'has-[a:focus]:outline-2',
            'has-[a:focus]:outline-color-core-color-4-SkyBlue',
            'transition-colors',
            'duration-200',
            'rounded-border-radius-radius-1',
            'px-spacing-spacing-12',
            'py-spacing-spacing-8',
            'w-full'
        ],
        tier2ToggleButton: [
            'p-2',
            'flex',
            'items-center',
            'justify-center',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'rounded-border-radius-radius-1'
        ],
        tier2FullToggleButton: [
            'flex',
            'items-center',
            'justify-between',
            'w-full',
            'px-spacing-spacing-12',
            'py-spacing-spacing-12',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'text-component-button-tertiary-text',
            'text-left',
            'hover:bg-component-header-flyoutmenu-mobile-hoverbg',
            'focus:bg-component-header-flyoutmenu-mobile-hoverbg',
            '!focus:ring-0',
            '!focus:ring-offset-0',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'rounded-border-radius-radius-1',
            'transition-colors',
            'duration-200'
        ],
        buttonItemIcon: [
            'duration-200',
            'h-auto',
            'transition-all',
            '!w-xs',
            'text-component-button-primary-icon',
            'group-hover:text-color-text-text',
            'group-focus:text-color-text-text',
            'fill-current'
        ],
        dropDownButton: [
            'cursor-pointer',
            'flex',
            'font-semibold',
            'gap-2',
            'items-start',
            'justify-between',
            '!place-items-center',
            'text-color-text-text',
            'text-typography-font-size-body-medium',
            'w-full',
            'group-hover:text-color-text-text',
            'group-focus:text-color-text-text'
        ],
        dropDownButtonText: [
            'flex',
            'text-color-text-text',
            'text-typography-font-size-body-medium'
        ],
        dropDownItem: [],
        dropDownItemName: [
            'font-semibold',
            'text-typography-font-size-body-small',
            'text-color-text-text-secondary',
            'text-sm'
        ],
        dropDownMenuGroup: [
            'flex',
            'flex-col',
            'gap-2',
            'py-4'
        ],
        dropDownMenuLinkWrapper: [
            'py-2',
            'flex',
            'items-center',
            'gap-2',
            'hover:underline',
            'focus:underline',
            'text-color-icon-icon',
            'hover:text-color-text-text',
            'focus:text-color-text-text',
            'group'
        ],
        dropDownMenuList: [
            'flex',
            'flex-col',
            'gap-2'
        ],
        dropDownMenuWrapper: [],
        hamburgerButton: [
            'flex',
            'p-spacing-spacing-8',
            'bg-color-core-color-4-SkyBlue',
            'rounded-general-border-radius-button',
            'items-center',
            'justify-center',
            'cursor-pointer',
            'hover:bg-color-text-white',
            'hover:text-color-core-color-1-Navy',
            'transition-colors',
            'duration-200',
            'text-color-core-color-1-Navy',
            'focus:bg-color-text-white',
            'focus:text-color-core-color-1-Navy',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'focus:outline-offset-0'
        ],
        hamburgerWrapper: [
            'flex',
            'items-center'
        ],
        searchButton: [
            'flex',
            'w-12',
            'h-12',
            'p-spacing-spacing-12',
            'justify-center',
            'items-center',
            'gap-spacing-spacing-8',
            'aspect-square',
            'rounded-general-border-radius-button',
            'border',
            'border-component-button-secondary-border',
            'bg-component-button-secondary-inverse-bg',
            'hover:bg-component-button-secondary-inverse-bg-hover',
            'focus:bg-component-button-secondary-inverse-bg-hover',
            'transition-colors',
            'duration-200'
        ],
        ctaButton: [
            'bg-color-core-color-4-SkyBlue',
            'text-color-text-white',
            'w-full',
            'px-6',
            'py-3',
            'rounded-general-border-radius-button',
            'font-semibold',
            'text-center',
            'transition-colors',
            'duration-200',
            'hover:bg-color-core-color-4-800',
            'focus:outline-none',
            'focus:outline-color-text-white'
        ],
        rightActionsWrapper: [
            'flex',
            'items-center',
            'gap-spacing-spacing-4'
        ],
        headerSection: [
            'flex',
            'justify-between',
            'py-spacing-spacing-8',
            'pl-spacing-spacing-12',
            'pr-spacing-spacing-8'
        ],
        languageWrapper: [
            'fixed',
            'right-0',
            'h-[calc(100vh-72px)]',
            'w-[85%]',
            'max-w-[400px]',
            'bg-white',
            'shadow-xl',
            'overflow-y-auto',
            'overscroll-contain',
            'touch-pan-y',
            'transition-[transform,opacity]',
            'duration-300',
            'ease-in-out',
            'z-50',
            'py-4',
            'flex',
            'flex-col',
            'rounded-tl-border-radius-radius-1',
            'rounded-bl-border-radius-radius-1'
        ],
        linkWrapper: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'text-component-button-tertiary-text',
            'gap-2',
            '!place-items-center',
            'flex',
            'justify-between',
            'items-center',
            'w-full',
            'hover:underline',
            'py-spacing-spacing-12',
            'px-spacing-spacing-12',
            'hover:bg-component-header-flyoutmenu-mobile-hoverbg',
            'focus:bg-component-header-flyoutmenu-mobile-hoverbg',
            'active:bg-color-core-color-4-SkyBlue',
            '!focus:ring-0',
            '!focus:ring-offset-0',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'rounded-border-radius-radius-1'
        ],
        homeButton: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'text-component-button-tertiary-text',
            'flex',
            'items-center',
            'w-full',
            'hover:underline',
            'py-spacing-spacing-12',
            'px-spacing-spacing-12',
            'hover:bg-component-header-flyoutmenu-mobile-hoverbg',
            'focus:bg-component-header-flyoutmenu-mobile-hoverbg',
            'active:bg-color-core-color-4-SkyBlue',
            '!focus:ring-0',
            '!focus:ring-offset-0',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'rounded-border-radius-radius-1'
        ],
        menuItems: [
            'flex',
            'flex-col',
            'm-0',
            'gap-spacing-spacing-4'
        ],
        menuNavItem: [
            'list-none',
            'm-0',
            'relative',
            'px-spacing-spacing-12',
            'py-spacing-spacing-4'
        ],
        wrapper: [
            'bg-color-core-color-1-Navy',
            'border-color-border-border',
            'border-b',
            'z-50',
            'sticky',
            'top-0'
        ],
        dropDownFeaturedWrapper: [
            'bg-color-surface-surface-secondary',
            'py-spacing-spacing-32',
            'gap-spacing-spacing-24',
            'overflow-hidden',
            'flex',
            'flex-col',
            'gap-1',
            'mt-4',
            '-mx-4',
            'px-4'
        ],
        dropDownFeaturedContent: [
            'flex',
            'flex-col'
        ],
        dropDownFeaturedTitle: [
            'font-typography-font-weight-medium',
            'text-typography-font-size-header-xSmall'
        ],
        dropDownFeaturedDescription: [
            'text-typography-font-size-body-small',
            'text-color-text-text-secondary',
            'font-typography-font-weight-body',
            'text-sm'
        ],
        dropDownFeaturedImg: [
            'h-auto',
            'w-full',
            'rounded-lg'
        ],
        linkContent: [
            'flex',
            'items-center',
            'gap-2',
            'w-full',
            'justify-between',
            'text-color-text-text'
        ],
        svgIconClass: [
            '!h-5',
            '!w-5',
            'transition-colors',
            'scale-x-0',
            'group-hover:scale-x-100',
            'group-focus:scale-x-100'
        ],
        featuredLink: [
            'flex',
            'items-center',
            'gap-2',
            'w-full',
            'justify-between',
            'text-color-text-text',
            'text-typography-font-size-body-medium',
            'hover:text-color-text-text',
            'focus:text-color-text-text'
        ],
        featuredLinkText: [
            'text-color-text-text',
            'text-typography-font-size-body-medium'
        ],
        navLinkText: [
            'text-typography-font-size-body-medium'
        ],
        mainNavSvgIconClass: [
            '!h-5',
            '!w-5',
            'transition-colors',
            'scale-x-100'
        ]
    },
    variants: {
        isOpen: {
            false: {
                wrapper: [],
                languageWrapper: [
                    'translate-x-full',
                    'pointer-events-none',
                    'opacity-0'
                ]
            },
            true: {
                wrapper: [],
                languageWrapper: [
                    'translate-x-0',
                    'pointer-events-auto',
                    'opacity-100'
                ]
            }
        },
        showTier2: {
            false: {
                tier1Panel: [
                    'translate-x-0',
                    'opacity-100'
                ],
                tier2Panel: [
                    'translate-x-full',
                    'opacity-0'
                ]
            },
            true: {
                tier1Panel: [
                    '-translate-x-full',
                    'opacity-0'
                ],
                tier2Panel: [
                    'translate-x-0',
                    'opacity-100'
                ]
            }
        },
        isRotated: {
            false: {
                buttonItemIcon: [
                    'rotate-180'
                ]
            },
            true: {
                buttonItemIcon: [
                    'rotate-0',
                    'text-color-icon-icon-selected'
                ]
            }
        },
        isMainNav: {
            true: {
                linkContent: [
                    'w-full',
                    'justify-between'
                ],
                svgIconClass: [
                    'scale-x-100'
                ]
            },
            false: {
                linkContent: [],
                svgIconClass: []
            }
        },
        isActive: {
            false: {},
            true: {
                tier1Button: [
                    'bg-color-core-color-4-SkyBlue'
                ],
                linkWrapper: [
                    'bg-color-core-color-4-SkyBlue'
                ]
            }
        },
        isHomeActive: {
            false: {},
            true: {
                homeButton: [
                    'bg-color-core-color-4-SkyBlue'
                ]
            }
        },
        showSearch: {
            false: {},
            true: {
                wrapper: [
                    '!border-b-0'
                ],
                searchBoxWrapper: [
                    '!border-t-0',
                    'border-b',
                    'border-color-border-border'
                ],
                searchButton: [
                    'bg-component-button-secondary-inverse-bg-hover'
                ]
            }
        }
    }
});
export default HeaderMobile;
